"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ImageAdRenderer from "./ImageAdRenderer";
import GamAdRenderer from "./GamAdRenderer";
import AdSenseRenderer from "./AdSenseRenderer";
import { trackAdEvent } from "@/lib/ads/telemetryClient";

export default function AdRenderer({ decision, onRenderState, onFailure, onRetire, placementIdentifier }) {
  const container = useRef(null);
  const sent = useRef(new Set());
  const [loaded, setLoaded] = useState(false);
  const emit = useCallback(type => { if ((!decision.expiresAt || Date.now() < decision.expiresAt) && decision.trackingToken && !sent.current.has(type)) { sent.current.add(type); trackAdEvent(decision.trackingToken, type); } }, [decision.trackingToken, decision.expiresAt]);
  const render = useCallback(success => {
    if (!success) { setLoaded(false); onRenderState?.(false); return; }
    setLoaded(true); onRenderState?.(true); emit("AD_FILL");
  }, [onRenderState, emit]);
  useEffect(() => {
    if (!loaded || !container.current) return;
    let timer;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        emit("IMPRESSION");
        if (!timer) timer = setTimeout(() => emit("VIEWABLE_IMPRESSION"), 1000);
      } else { clearTimeout(timer); timer = null; }
    }, { threshold: [0.5] });
    observer.observe(container.current);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [loaded, emit]);
  if (!decision || decision.format === "NO_FILL") return null;
  return <div ref={container} className="flex w-full justify-center" aria-label="Advertisement" onClickCapture={() => { if (loaded && ["IMAGE", "LINK"].includes(decision.format)) emit("CLICK"); }}>
    {decision.format === "GAM" ? <GamAdRenderer decision={decision} onRenderState={render} onFailure={onFailure} onRetire={onRetire} placementIdentifier={placementIdentifier} /> :
      decision.format === "ADSENSE" ? <AdSenseRenderer decision={decision} publicationId={decision.publicationId} placementIdentifier={placementIdentifier} onRenderState={render} onRetire={onRetire} onFailure={onFailure} /> :
      ["IMAGE", "LINK"].includes(decision.format) ? <ImageAdRenderer decision={decision} onRenderState={render} onFailure={onFailure} /> : null}
  </div>;
}
